import { AttestationModal } from '@app/components'
import { useAttestationPeriods } from '@app/lib'
import { SugarAttestationPeriod } from '@app/types'
import { ActionIcon, Box, Center, createStyles, Group, LoadingOverlay, Table, Text } from '@mantine/core'
import { IconCheck, IconEdit, IconLock, IconLockOpen } from '@tabler/icons-react'
import { map } from 'lodash'
import { type FC, useCallback, useState } from 'react'

const useStyles = createStyles(() => ({
  lockWidth: {
    width: 50,
    whiteSpace: 'nowrap'
  },
  noTopBorder: {
    borderTopWidth: 0
  },
  fitWidth: {
    width: 1,
    whiteSpace: 'nowrap'
  }
}))

export interface AttestationPeriodsTableProps {
  schoolYearId: string | null
}

export const AttestationPeriodsTable: FC<AttestationPeriodsTableProps> = ({ schoolYearId }) => {
  const { classes } = useStyles()
  const [editing, setEditing] = useState<SugarAttestationPeriod | null>(null)
  const { data: attestationPeriods, isFetching: isFetchingAttestationPeriods } = useAttestationPeriods(schoolYearId)
  const isLocked = editing?.attestationLocked ?? false

  return (
    <Box pos='relative'>
      <LoadingOverlay visible={isFetchingAttestationPeriods} />
      <Table
        className={classes.noTopBorder}
        horizontalSpacing='xs'
        mb='xl'
        verticalSpacing='xs'
        withBorder
      >
        <thead>
          <tr>
            <th className={classes.lockWidth}></th>
            <th>Period</th>
            <th className={classes.fitWidth}>Submitted</th>
            <th className={classes.fitWidth}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {map(attestationPeriods?.data, (attestationPeriod, attestationPeriodIdx) => (
            <tr key={`attestation-period-${attestationPeriodIdx}`}>
              <td className={classes.lockWidth}>
                <Center>
                  {attestationPeriod.attestationLocked || attestationPeriod.attestationPeriodLocked ? (
                    <IconLock size={16} />
                  ) : (
                    <IconLockOpen size={16} />
                  )}
                </Center>
              </td>
              <td>{attestationPeriod.attestationPeriodName}</td>
              <td className={classes.fitWidth}>
                <Center>
                  {attestationPeriod.attestationId ? (
                    <IconCheck
                      color='green'
                      size={16}
                    />
                  ) : (
                    '-'
                  )}
                </Center>
              </td>
              <td className={classes.fitWidth}>
                <Center>
                  <ActionIcon
                    color='navy'
                    onClick={() => setEditing(attestationPeriod)}
                    radius='xl'
                    tabIndex={0}
                    title='Edit Attestation Period'
                    variant='filled'
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Center>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <AttestationModal
        attestationPeriodId={editing?.attestationPeriodId}
        opened={!!editing}
        onClose={useCallback(() => setEditing(null), [])}
        title={
          <Group
            align='center'
            spacing='xs'
          >
            {isLocked ? <IconLock size={16} /> : null}
            <Text>
              {isLocked ? 'LOCKED - ' : ''}Monthly Attestation - {editing?.attestationPeriodName}
            </Text>
          </Group>
        }
      />
    </Box>
  )
}
